<template>
  <div class="step-card" :class="stepClasses" @click="handleCardClick">
    <div class="step-header">
      <div class="step-number" v-if="!compact">
        <v-icon v-if="step.isFinish" color="light" :large="!isMobile"
          >emoji_events</v-icon
        >
        <template v-else>
          {{ stepNumber }}
        </template>
      </div>
      <div class="step-info">
        <h3 class="step-title">{{ step.title }}</h3>
        <p class="step-description">
          {{ step.description }}
          <smart-tooltip
            v-if="step.help"
            :category="step.help.category"
            :type="step.help.slug"
            small
          />
        </p>

        <!-- Dynamic Info Display - Only show when step is completed -->
        <template v-if="$slots.data && completed">
          <slot name="data" />
        </template>

        <!-- Dialogs - Always available -->
        <template v-if="$slots.dialogs">
          <slot name="dialogs" />
        </template>

        <div class="step-actions mt-4">
          <v-btn
            v-if="completed"
            color="primary"
            outlined
            @click.stop="handleAction"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit {{ step.editText || step.title }}
          </v-btn>

          <template v-else>
            <v-btn
              v-if="step.buttonText"
              :color="buttonColor"
              :disabled="!canActivate"
              :small="compact"
              @click.stop="handleAction"
            >
              <v-icon left>{{ step.buttonIcon }}</v-icon>
              {{ step.buttonText }}
            </v-btn>

            <div v-else class="no-action-message">
              <v-icon color="info" class="mr-2">mdi-information</v-icon>
              <span>Check your email and click the verification link</span>
            </div>

            <v-btn
              v-if="isCurrentStep && !step.isFinish && !compact"
              color="primary"
              text
              @click.stop="skipStep"
              class="ml-2"
            >
              SKIP
            </v-btn>
          </template>
        </div>
      </div>
      <div class="step-status" v-if="!compact">
        <v-icon v-if="completed" color="success" large>mdi-check-circle</v-icon>
        <v-icon v-else-if="isCurrentStep" color="primary" large
          >mdi-arrow-right</v-icon
        >
        <v-icon v-else color="disabled" large>mdi-lock</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import SmartTooltip from '@/components/basic/smart-tooltip/SmartTooltip.vue';

export default {
  name: 'LaunchStepCard',
  components: { SmartTooltip },

  props: {
    step: {
      type: Object,
      required: true,
    },
    completed: {
      type: Boolean,
      default: false,
    },
    isCurrentStep: {
      type: Boolean,
      default: false,
    },
    canActivate: {
      type: Boolean,
      default: false,
    },
    extraData: {
      type: Object,
      default: () => ({}),
    },
    compact: {
      type: Boolean,
      default: false,
    },
    stepNumber: {
      type: [String, Number],
      default: '',
    },
    homeMode: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    buttonColor() {
      return this.step.isFinish ? 'success' : 'primary';
    },

    stepClasses() {
      const classes = ['step-card'];

      if (this.completed) {
        classes.push('step-completed');
      } else if (this.isCurrentStep || this.homeMode) {
        classes.push('step-current');
      } else {
        classes.push('step-future');
      }

      if (this.step.isFinish) {
        classes.push('step-finish');
      }

      if (this.compact) {
        classes.push('compact');
      }

      return classes;
    },
  },

  methods: {
    handleAction() {
      this.$emit('action', this.step);
    },

    handleCardClick() {
      if (this.canActivate) {
        this.$emit('step-clicked', this.step.id);
      }
    },

    skipStep() {
      this.$emit('skip', this.step.id);
    },

    getSummaryText() {
      if (this.step.isFinish) {
        return this.step.summaryText || 'Setup completed!';
      }

      return this.step.summaryText || `${this.step.title} completed`;
    },
  },
};
</script>

<style lang="less" scoped>
.step-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid var(--v-success-lighten4);
  border-radius: 8px;
  padding: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: @base-shadow;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--gradient-simple);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(27, 192, 175, 0.2);
    border-color: #1bc0af;

    &::before {
      opacity: 1;
    }
  }

  &.step-current {
    border-color: #1bc0af;
    box-shadow: 0 4px 16px rgba(27, 192, 175, 0.15);

    &::before {
      opacity: 1;
    }
  }

  &.step-completed {
    background: rgba(255, 255, 255, 0.98);

    &::before {
      background: linear-gradient(
        90deg,
        var(--v-success-base),
        var(--v-success-lighten2)
      );
      opacity: 1;
    }
  }

  &.step-future {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  &.step-finish {
    &.step-current {
      background: var(--gradient);
      border-color: var(--v-success-lighten2);

      &::before {
        background: var(--gradient-simple);
      }

      .step-title {
        color: #fff;
      }

      .step-description {
        color: #fff;
        opacity: 0.9;
      }

      .step-status {
        .v-icon {
          color: #fff !important;
        }
      }

      .step-number {
        background: rgba(255, 255, 255, 0.3);
        color: #fff;
      }
    }
  }
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.step-number {
  width: 48px;
  height: 48px;
  background: var(--gradient-simple);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(27, 192, 175, 0.25);
}

.step-info {
  flex: 1;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.step-description {
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.step-status {
  flex-shrink: 0;
}

.step-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--v-success-base);
  font-weight: 500;
  margin-bottom: 16px;
}

.step-summary-text {
  font-size: 16px;
  font-weight: bold;
}

.step-actions {
  display: flex;
  align-items: center;
  gap: 12px;

  .v-btn--text {
    color: #666;
    font-weight: 500;

    &:hover {
      background-color: rgba(102, 102, 102, 0.1);
    }
  }
}

@media (max-width: 768px) {
  .step-card {
    padding: 16px;
  }

  .step-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    margin-bottom: 8px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .step-title {
    font-size: 18px;
    margin-bottom: 6px;
  }

  .step-description {
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 8px;
  }

  .step-actions {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .step-actions .v-btn {
    width: 100%;
    height: 40px;
  }

  .step-summary {
    justify-content: center;
    margin-bottom: 12px;
  }

  .step-summary-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .step-card {
    padding: 12px;
  }

  .step-status {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .step-number {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
}

// Compact mode styles
.step-card.compact {
  padding: 24px;
  border: 1px solid var(--v-borderColor-base) !important;
  box-shadow: @base-shadow !important;

  .step-title {
    font-size: 18px;
    color: #1a1a1a;
  }

  .step-description {
    font-size: 14px;
    line-height: 1.5;
  }
}

// No action message styles
.no-action-message {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 4px;
  color: #1976d2;
  font-size: 14px;
  font-weight: 500;
}
</style>
